import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Sender from '../Sender';
import AdvancedSignaturePopUp from '../../formsCommon/AdvancedSignaturePopUp';
import UserCredentialsOverlay from './UserCredentialsOverlay';
import RestoreSavedDocumentsDialog from '../RestoreSavedDocumentsDialog';
import RejectDocumentPopUp from '../RejectDocumentPopUp';
import MainInfoPopUp from '../MainInfoPopUp';
import { gaeApp } from '../../helpers/Analytics';
import { onBrowserClose, DOC_TYPE_INFORMATION } from '../../helpers/utils';
import { f7navigator } from '../../helpers/navigator';
import {
  setOptVerifyDone,
  setRequestStepSubmitted,
  setScanEnded,
  stopSendRequest
} from '../../../redux/actions/rootAction';
import { isSendRequestActive } from '../../../redux/sendRequestTask';
import { selectPreparedState } from '../../../redux/selectors/preparedState';

function RequestOverlays(props) {
  const {
    recipientRequestId,
    requestedDocuments,
    sendRequestTask,
    needAesSignature,
    optRequired,
    optVerifyDone,
    demoRequest,
    setOptVerifyDone: setOptVerifyDoneAction,
    setRequestStepSubmitted: setRequestStepSubmittedAction,
    setScanEnded: setScanEndedAction,
    stopSendRequest: stopSendRequestAction
  } = props;

  const activeTask = isSendRequestActive(sendRequestTask)
    ? sendRequestTask
    : null;

  function handleStopSend() {
    gaeApp('SEND_REQUEST.END');
    stopSendRequestAction();
  }

  function handlePostSendNavigation() {
    if (activeTask?.skipThankyou) {
      return;
    }

    if (activeTask?.fromDocView) {
      gaeApp('SENDER_DONE_CALLBACK');
      if (window.isDesktop) {
        f7navigator.forceBackToList({ showThankyouPopUp: true });
      } else {
        f7navigator.toThankYou();
      }
      return;
    }

    const onlyInfoDocuments =
      requestedDocuments.length === 1 &&
      requestedDocuments.every(doc => doc.type === DOC_TYPE_INFORMATION);

    if (!window.isDesktop && !onlyInfoDocuments) {
      f7navigator.toThankYou();
    }
  }

  function handleSendDone() {
    if (activeTask?.tillPage) {
      gaeApp('SEND_REQUEST_TILL_PAGE_DONE');
      setRequestStepSubmittedAction(activeTask.tillPage);
      handleStopSend();
      return;
    }

    gaeApp('SEND_REQUEST_DONE');
    handleStopSend();
    gaeApp('SET_SCAN_ENDED_TO_TRUE');
    setScanEndedAction();
    window.removeEventListener('beforeunload', onBrowserClose);
    handlePostSendNavigation();
  }

  useEffect(() => {
    if (activeTask && demoRequest) {
      handleSendDone();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTask, demoRequest]);

  const showAesPopUp = activeTask && needAesSignature && !optVerifyDone;
  const showOptPopUp = activeTask && optRequired && !optVerifyDone;
  const optPopupRequired = showAesPopUp || showOptPopUp;
  const requestLoaded =
    Boolean(recipientRequestId) && Boolean(requestedDocuments?.length);

  return (
    <>
      <MainInfoPopUp />
      {requestLoaded && (
        <>
          <RestoreSavedDocumentsDialog />
          <RejectDocumentPopUp />
          <UserCredentialsOverlay />
          <AdvancedSignaturePopUp
            opened={optPopupRequired}
            onClose={handleStopSend}
            onDone={() => setOptVerifyDoneAction()}
            aesOptMode={showAesPopUp}
            requestedDocuments={requestedDocuments}
          />
          {activeTask && !optPopupRequired && !demoRequest && (
            <Sender
              noProgressDialog={activeTask.noProgressDialog}
              onDone={handleSendDone}
              onError={handleStopSend}
            />
          )}
        </>
      )}
    </>
  );
}

RequestOverlays.propTypes = {
  recipientRequestId: PropTypes.string,
  requestedDocuments: PropTypes.array,
  sendRequestTask: PropTypes.object,
  needAesSignature: PropTypes.bool,
  optRequired: PropTypes.bool,
  optVerifyDone: PropTypes.bool,
  demoRequest: PropTypes.bool,
  setOptVerifyDone: PropTypes.func,
  setRequestStepSubmitted: PropTypes.func,
  setScanEnded: PropTypes.func,
  stopSendRequest: PropTypes.func
};

const mapStateToProps = state => {
  const { requestedDocuments } = selectPreparedState(state);

  return {
    recipientRequestId: state.recipientRequestId,
    requestedDocuments,
    sendRequestTask: state.sendRequestTask,
    needAesSignature: requestedDocuments.some(
      d => d.curentRecipientAdvancedSignaturesList?.length > 0
    ),
    optVerifyDone: state.optVerifyDone,
    optRequired:
      state.authenticationState === 'REQUIRED' &&
      state.authenticationType === 'SMS_TAN',
    demoRequest: state.demoRequest
  };
};

const mapDispatchToProps = dispatch => ({
  setOptVerifyDone: () => dispatch(setOptVerifyDone()),
  setRequestStepSubmitted: pageId => dispatch(setRequestStepSubmitted(pageId)),
  setScanEnded: () => dispatch(setScanEnded()),
  stopSendRequest: () => dispatch(stopSendRequest())
});

export default connect(mapStateToProps, mapDispatchToProps)(RequestOverlays);
